import {onMounted, onUnmounted, ref} from 'vue'
import {useSessionStore, useAppStore, useUserStore} from '@/stores'
import {useMeetingStore} from '@/stores'
import {useRecordingStore} from '@/stores'
import useNetworkMachine from '@/state/useNetworkMachine'
import {useStreamStore} from '@/stores/streamStore'
import {jsonRpc} from '@communication'
import {useSnapshot} from '@/composables/useSnapshot'
import {useEventBus} from '@vueuse/core'
import {useRecording} from './useRecording'

const env = import.meta.env

// Role of the party that dropped (or came back) last, shown in the network toast of MeetingView
const roleLeave = ref('')

export const useParticipantDisconnected = () => ({roleLeave})

export const useParticipantDisconnectedCallback = () => {
  const {stop: stopRecording} = useRecording()
  const {dismiss} = useSnapshot()
  let graceTimeout: ReturnType<typeof setTimeout> | undefined

  const onJsonRpcDisconnect = (event: CustomEvent) => {
    const {id: participantId} = event.detail
    const session = useSessionStore()
    const participant = session.getParticipant(participantId)
    // Unknown or already removed (a second 'disconnected' for the same party): nothing to announce
    if (!participant) return
    session.addParticipantsLeave(participant.party + '_' + participantId)
    roleLeave.value = participant.party
    if (participant.party === 'client' || participant.party === 'assistant') {
      const app = useAppStore()
      if (app.isLoading) {
        app.loaded()
      }
      if (useRecordingStore().isOn && participant.party === 'client') {
        stopRecording()
      }
      if (useMeetingStore().isDraw) {
        useEventBus('board:signal:snapshot:save')
        dismiss('snapshot')
      }

      useMeetingStore().toLobby()
      session.removeParticipant(participantId)
    } else {
      session.removeParticipant(participantId)
    }
    const {send} = useNetworkMachine()
    send('LOST_CONNECTION')
  }
  onMounted(() => {
    jsonRpc.on('disconnected', onJsonRpcDisconnect)
  })
  onUnmounted(() => {
    jsonRpc.off('disconnected', onJsonRpcDisconnect)
  })

  const moveToExit = async () => {
    console.error(`Grace timeout ${jsonRpc.status}`)
    const streamStore = useStreamStore()
    // Only a call is left; before joining (an outage on the join screen) the camera and mic stay on
    if (jsonRpc.status === 'DISCONNECTED' && useAppStore().isCall) {
      const app = useAppStore()
      streamStore.stopAll()
      app.goToEnd()
    }
  }

  const handleRejoined = () => {
    const session = useSessionStore()
    const userStore = useUserStore()
    const participantLeave = userStore.party + '_' + userStore.id
    session.removeParticipantsLeave(participantLeave)

    const {send} = useNetworkMachine()
    send('ONLINE_CONNECTION')
  }

  useEventBus('websocket:reconnecting').on(() => {
    const {send} = useNetworkMachine()
    send('LOST_CONNECTION')

    const session = useSessionStore()
    const userStore = useUserStore()
    session.addParticipantsLeave(userStore.party + '_' + userStore.id)
    graceTimeout = setTimeout(async () => moveToExit(), env.VITE_GRACE_TIMEOUT)
  })

  useEventBus('websocket:reconnected').on(() => {
    clearTimeout(graceTimeout)
    // In a call an open socket is not enough: "reconnecting" stays until the re-join has succeeded, which JsonRpc retries
    // for up to about 30 s while the server still holds the old socket. Elsewhere there is nothing to re-join
    if (!useAppStore().isCall) {
      handleRejoined()
    }
  })

  useEventBus('websocket:rejoined').on(handleRejoined)
}
